import { useMissionControlDataFlowData, toggleChatHistory } from '@stores/data.store';
import { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageStructure, { PageContent, PageContentHeader, PageContentInner, Pane, PaneContent } from './PageStructure.component';
import PliableLoader from '@components/loaders/PliableLoader.component';
import LoadingCard from '@components/card/LoadingCard.component';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { Thread } from '@components/assistant-ui/thread';
import { Modal } from 'react-bootstrap';
import toast from '@services/toast.service';
import { useFeatureFlagValue } from '@services/featureFlags/featureFlag.service';

import "@assistant-ui/styles/index.css";
import { useCustomLangGraphRuntime } from '@services/langGraphRuntime';
import { MissionControlDataFlowNode, MissionControlDataResponse, ChatThread } from '@stores/data.store';
import { confirmRunBuild } from '@components/Inbox.component';
import { useQueryParams } from '@services/url.service';
import { DashboardIcon } from '@components/pipelineNodes/PipelineNodeIcon.component';
import Dropdown from '@components/form/Dropdown.component';
import { shortid } from '@services/id.service';
import { ChatHistoryList } from '@components/chat/ChatHistoryList.component';



type AgentType = 'build_chart' | 'analyst';

// Wrapper component that renders the assistant with a specific thread ID
// This component will remount when the threadId changes (via key prop)
const AssistantWrapper = ({
    agentType,
    model,
    threadId,
    initialQuestion,
    onAnalysisGenerated,
}: {
    agentType: AgentType;
    model: string;
    threadId: string;
    initialQuestion: string;
    onAnalysisGenerated: (analysisId: string) => void;
}) => {
    const { runtime, isHistoricalThread, isLoaded } = useCustomLangGraphRuntime({
        agentType,
        model,
        threadId,
    });

    // Track which tool calls we've already processed to avoid duplicates
    const processedToolCallsRef = useRef<Set<string>>(new Set());

    // Auto-run charts for:
    // - New threads: always enabled
    // - Historical threads: only after user sends a new message (detected via isRunning)
    const [userHasInteracted, setUserHasInteracted] = useState(false);

    // Enable auto-run for new threads, or historical threads after user interaction
    const autoRunEnabled = !isHistoricalThread || userHasInteracted;

    useEffect(() => {
        const unsubscribe = runtime.thread.subscribe(() => {
            try {
                const state = runtime.thread.getState();
                if (!state) {
                    return;
                }

                // Detect user interaction on historical threads (when streaming starts)
                if (isHistoricalThread && state.isRunning && !userHasInteracted) {
                    setUserHasInteracted(true);
                }

                if (!state.messages) {
                    return;
                }
                const msgs = state.messages;
                if (msgs.length === 0) {
                    return;
                }
            } catch (err) {
                // Ignore errors during state transitions
                console.debug('Thread state not ready:', err);
            }
        });
        return () => unsubscribe?.();
    }, [runtime, isHistoricalThread, userHasInteracted]);

    // Show loading state while loading historical thread
    if (isHistoricalThread && !isLoaded) {
        return <LoadingCard action="Loading conversation" />;
    }

    return (
        <AssistantRuntimeProvider runtime={runtime}>
            <Thread
                initialQuestion={initialQuestion}
                toolCallHandlers={{
                    onAnalysisGenerated,
                }}
                autoRunCharts={autoRunEnabled}
            />
        </AssistantRuntimeProvider>
    );
};

const ChartAssistantPage = () => {
    const missionControl = useMissionControlDataFlowData();
    const [assistantMode, setAssistantMode] = useState<AgentType>('build_chart');
    const [model, setModel] = useState('claude-3-5-sonnet');

    // Use URL route param for threadId
    const { threadId: urlThreadId } = useParams<{ threadId?: string }>();
    const navigate = useNavigate();

    // Generate a stable default thread ID (only once per component mount)
    const defaultThreadId = useMemo(() => shortid(), []);

    // Get thread_id from URL or use the default
    const currentThreadId = urlThreadId || defaultThreadId;

    // Update URL when thread ID changes
    const setCurrentThreadId = useCallback((threadId: string) => {
        navigate(`/ellie/${threadId}`, { replace: true });
    }, [navigate]);

    // Redirect to URL with thread_id if not present
    useEffect(() => {
        if (!urlThreadId) {
            navigate(`/ellie/${defaultThreadId}`, { replace: true });
        }
    }, [urlThreadId, defaultThreadId, navigate]);

    const enableEllieModes = useFeatureFlagValue('enable-ellie-modes', false);
    const enableEllieModelSelection = useFeatureFlagValue('ellie-model-selector', false);

    const resetThread = useCallback(() => {
        setCurrentThreadId(shortid());
    }, [setCurrentThreadId]);

    const [loading, setLoading] = useState(false);
    const [resultSql, setResultSql] = useState('');
    const [error, setError] = useState('');

    // Check if there are nodes that need building with explicit type safety
    const missionData = missionControl.data as MissionControlDataResponse | undefined;
    const nodesThatNeedBuilding: MissionControlDataFlowNode[] = (missionData?.nodes || []).filter((node: MissionControlDataFlowNode) => node.data.needsBuilding === true);
    const hasUnbuildNodes: boolean = nodesThatNeedBuilding.length > 0;

    // State to track if nodes list is expanded
    const [nodesListExpanded, setNodesListExpanded] = useState(false);

    
    // State to control modal visibility
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [showModeModal, setShowModeModal] = useState(false);



    const changeAssistantMode = useCallback((newMode: AgentType) => {
        setAssistantMode(newMode);
        setAnalysisIds([]);
        setActiveAnalysisId('');
        setShowModeModal(false);

        // If it's Analyst, set the model to Claude 4 Sonnet, otherwise use Claude 3.5 sonnet
        if (newMode === 'analyst') {
            setModel('claude-4-sonnet');
        } else {
            setModel('claude-3-5-sonnet');
        }

        resetThread();
    }, []);

    

    const modeConfig = {
        build_chart: {
            label: 'Chart Builder',
            icon: 'mdi-chart-bar',
            description: 'Build custom charts and visualizations from your data with interactive guidance.'
        },
        analyst: {
            label: 'Analyst',
            icon: 'mdi-brain',
            description: 'Ask questions and get insights about your data through natural conversation. Note: this will consume more Snowflake credits.'
        }
    };

    const [analysisIds, setAnalysisIds] = useState<string[]>([]);
    
    // Grab the initialQuestion query param and set it to a state variable called initialQuestion

    const [initialQuestion, setInitialQuestion] = useState('');
    
    const [activeAnalysisId, setActiveAnalysisId] = useState('');
    const sideContainerRef = useRef<HTMLDivElement>(null);
    const activeElementRef = useRef<HTMLDivElement>(null);

    const updateInitialQuestion = useCallback(() => {
        setInitialQuestion(queryParams.get('initialQuestion') || '');
    }, []);
    
    // Auto-open modal when unbuilt nodes are detected, close when none need building
    useEffect(() => {
        // Don't do anything while loading OR fetching (prevents flash from stale data)
        if (missionControl.isLoading || missionControl.isFetching) {
            return;
        }
        
        // Don't show the modal if there was an error fetching the data
        if (missionControl.error) {
            setShowWarningModal(false);
            return;
        }
        
        if (hasUnbuildNodes) {
            setShowWarningModal(true);
        } else {
            setShowWarningModal(false);
            updateInitialQuestion();
        }
    }, [hasUnbuildNodes, missionControl.isLoading, missionControl.isFetching, missionControl.error]);

    // Show toast notification for mission control errors
    useEffect(() => {
        if (missionControl.error) {
            toast('danger', 'Error', 'Failed to check node build status. Please refresh the page.');
        }
    }, [missionControl.error]);

    useEffect(() => {
        if (activeElementRef && activeElementRef.current && sideContainerRef.current) {
                const elementTop = activeElementRef.current?.offsetTop;

                // delay to ensure rendering is complete before scrolling
                setTimeout(() => {
                    sideContainerRef.current?.scrollTo({
                        top: elementTop,
                        behavior: 'smooth'
                    });
                }, 1000);
        }
    }, [activeAnalysisId]);

    const handleCloseModal = () => {
        setShowWarningModal(false);
        updateInitialQuestion();
    };

    const queryParams = useQueryParams();

 

    const handleRefreshData = async () => {
        setShowWarningModal(false);
        await confirmRunBuild(() => {
            updateInitialQuestion();
        });
    };

    return <PageStructure pageTitle="Data Assistant">
        <PageContent>
            {/* Warning Modal for unbuilt nodes */}
            <Modal show={showWarningModal} onHide={handleCloseModal} backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="mdi mdi-alert-circle text-warning me-2"></i>
                        Data Refresh Required
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-3">
                        There {nodesThatNeedBuilding.length === 1 ? 'is' : 'are'} <strong>{nodesThatNeedBuilding.length}</strong> node{nodesThatNeedBuilding.length === 1 ? '' : 's'} that need{nodesThatNeedBuilding.length === 1 ? 's' : ''} building before you can use Ellie.
                    </p>
                    
                    <div className="mb-3">
                        <strong>Nodes requiring build:</strong>
                        <ul className="mt-2 mb-0">
                            {nodesThatNeedBuilding.slice(0, 5).map(node => (
                                <li key={node.id}>{node.data.title}</li>
                            ))}
                            {nodesThatNeedBuilding.length > 5 && !nodesListExpanded && (
                                <li>
                                    <button
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        onClick={() => setNodesListExpanded(true)}
                                        style={{ color: 'var(--ct-primary)' }}
                                    >
                                        Show {nodesThatNeedBuilding.length - 5} more...
                                    </button>
                                </li>
                            )}
                            {nodesThatNeedBuilding.length > 5 && nodesListExpanded && (
                                <>
                                    {nodesThatNeedBuilding.slice(5).map(node => (
                                        <li key={node.id}>{node.data.title}</li>
                                    ))}
                                    <li>
                                        <button
                                            className="btn btn-link btn-sm p-0 text-decoration-none"
                                            onClick={() => setNodesListExpanded(false)}
                                            style={{ color: 'var(--ct-primary)' }}
                                        >
                                            Show less
                                        </button>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                    
                    <p className="text-muted mb-0">
                        Please refresh your data to ensure Ellie works with the latest information.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-light me-1" onClick={handleCloseModal}>Close</button>
                    <button className="btn btn-pliable" onClick={handleRefreshData}>
                        <i className="mdi mdi-refresh me-2"></i>
                        Refresh Data
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Mode Selection Modal */}
            <Modal show={showModeModal} onHide={() => setShowModeModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select Agent Mode</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row g-3">
                        {(Object.keys(modeConfig) as AgentType[]).map((mode) => {
                            const config = modeConfig[mode];
                            const isActive = assistantMode === mode;
                            return (
                                <div key={mode} className="col-12">
                                    <div 
                                        className={`card h-100 ${isActive ? 'border-primary' : ''}`}
                                        onClick={() => changeAssistantMode(mode)}
                                        style={{ 
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            borderWidth: isActive ? '2px' : '1px'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.borderColor = '#dee2e6';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isActive) {
                                                e.currentTarget.style.borderColor = '';
                                                e.currentTarget.style.boxShadow = '';
                                            }
                                        }}
                                    >
                                        <div className="card-body">
                                            <div className="d-flex align-items-start">
                                                <div className="me-3">
                                                    <i 
                                                        className={`mdi ${config.icon}`}
                                                        style={{ 
                                                            fontSize: '2.5rem',
                                                            color: isActive ? 'var(--ct-primary)' : '#6c757d'
                                                        }}
                                                    ></i>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h5 className="mb-2">
                                                        {config.label}
                                                        {isActive && (
                                                            <i className="mdi mdi-check-circle text-primary ms-2"></i>
                                                        )}
                                                    </h5>
                                                    <p className="text-muted mb-0">{config.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
            </Modal>

            <PageContentHeader>
                <div className="d-flex center-vertically">
                    <DashboardIcon
                        icon="mdi mdi-robot"
                        bgColor="pliable"
                    />
                    <div>
                        <h1 className="mb-0">Ask Ellie</h1>
                    </div>
                    <div className="ms-3 text-muted">
                        <i className="mdi mdi-information-outline"></i> AI can make mistakes. Always review analysis before sharing or acting on results.
                    </div>
                    <div className="flex-1"></div>
                    {enableEllieModes && (
                        <div 
                            onClick={() => setShowModeModal(true)}
                            style={{ cursor: 'pointer' }}
                            className="d-flex btn btn-light align-items-center ms-3"
                        >
                            <i className={`mdi ${modeConfig[assistantMode].icon} me-2`}></i>
                            <div>
                                <span className="text-muted">Agent Mode: </span>
                                <span className="fw-bold">{modeConfig[assistantMode].label}</span>
                            </div>
                            <i className="mdi mdi-chevron-down ms-2"></i>
                        </div>
                    )}
                    {enableEllieModelSelection && <div className="ms-2">
                        <Dropdown
                            variant="button"
                            selected={model}
                            options={[
                                { label: 'Claude 3.5 Sonnet', value: 'claude-3-5-sonnet' }, 
                                { label: 'Claude 3.7 Sonnet', value: 'claude-3-7-sonnet' }, 
                                // { label: 'Claude 4 Opus', value: 'claude-4-opus' },
                                { label: 'Claude 4 Sonnet', value: 'claude-4-sonnet' },
                                { label: 'OpenAI GPT 4.1', value: 'openai-gpt-4.1' },
                            ]}
                            onChange={(value) => setModel(value)}
                            btnVariant="light"

                        />
                    </div> }
                </div>
            </PageContentHeader>
            <PageContentInner hasHeader>
                <div className="d-flex h-100" style={{ overflow: 'hidden' }}>
                    <div className="h-100 flex-grow-1" style={{ minWidth: 0 }}>
                        <Pane>
                            <PaneContent>
                                {loading && <PliableLoader />}
                                {error && <div className="alert alert-danger">{error}</div>}
                                {resultSql && <div className="alert alert-success">{resultSql}</div>}
                                {!loading && (
                                    <AssistantWrapper
                                        key={currentThreadId}
                                        agentType={assistantMode}
                                        model={model}
                                        threadId={currentThreadId}
                                        initialQuestion={initialQuestion}
                                        onAnalysisGenerated={(analysisId) => {
                                            if (analysisId && !analysisIds.includes(analysisId)) {
                                                setAnalysisIds([...analysisIds, analysisId]);
                                            }
                                            setActiveAnalysisId(analysisId);
                                        }}
                                    />
                                )}
                            </PaneContent>
                        </Pane>
                    </div>
                    <div
                        className="h-100 border-start"
                        style={{
                            width: '325px',
                            flexShrink: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <ChatHistoryList
                            onSelectThread={(thread: ChatThread) => {
                                navigate(`/ellie/${thread.thread_id}`);
                            }}
                            onNewChat={() => {
                                navigate(`/ellie/${shortid()}`);
                            }}
                            activeThreadId={currentThreadId}
                        />
                    </div>
                </div>
            </PageContentInner>

            
        </PageContent>
    </PageStructure>
}

export default ChartAssistantPage;
